import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FiLogOut, FiUser, FiChevronDown } from 'react-icons/fi';
import api from '../services/api';
import MyProfile from './MyProfile';
import '../styles/Navbar.css';

const Navbar = () => {
  const { user, logout, updateUserStatus } = useAuth();
  const navigate = useNavigate();
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(user?.status || 'Not Available');
  const [attendanceStatus, setAttendanceStatus] = useState("Not Started");
  const [loadingAttendance, setLoadingAttendance] = useState(false);
  const [showProfile, setShowProfile] = useState(false);

  // Sync status from context (e.g. when changed from profile modal)
  useEffect(() => {
    if (user?.status) setCurrentStatus(user.status);
  }, [user?.status]);

  const statuses = ['Working', 'On Break', 'Busy', 'Not Available'];

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout', { reason: 'manual' });
    } catch (error) {
      console.error('Logout error:', error);
    }
    logout();
    navigate('/login');
  };

  const handleStatusChange = async (status) => {
    try {
      await api.put('/users/status', { status });
      setCurrentStatus(status);
      if (updateUserStatus) {
        updateUserStatus(status);
      }
      setShowStatusMenu(false);
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleClockIn = async () => {
    try {
      setLoadingAttendance(true);
      const res = await api.post("/users/session/start");
      setAttendanceStatus("Clocked In");
      alert(res.data.message || "Clock In Successful");
    } catch (error) {
      alert(error.response?.data?.message || "Clock In Failed");
      console.error("Clock In error:", error);
    } finally {
      setLoadingAttendance(false);
    }
  };

  const handleClockOut = async () => {
    try {
      setLoadingAttendance(true);
      const res = await api.post("/users/session/end");
      setAttendanceStatus("Clocked Out");
      alert(res.data.message || "Clock Out Successful");
    } catch (error) {
      alert(error.response?.data?.message || "Clock Out Failed");
      console.error("Clock Out error:", error);
    } finally {
      setLoadingAttendance(false);
    }
  };

  useEffect(() => {
    const fetchTodaySession = async () => {
      try {
        const res = await api.get("/users/session/today");

        if (res.data.sessions.length > 0) {
          const latest = res.data.sessions[0];

          if (latest.logoutTime === null) {
            setAttendanceStatus("Clocked In");
          } else {
            setAttendanceStatus("Clocked Out");
          }
        } else {
          setAttendanceStatus("Not Started");
        }

      } catch (error) {
        console.error("Error fetching attendance:", error);
      }
    };

    fetchTodaySession();
  }, []);


  const getStatusColor = (status) => {
    switch (status) {
      case 'Working':
        return '#10b981';
      case 'On Break':
        return '#f59e0b';
      case 'Busy':
        return '#ef4444';
      case 'Not Available':
        return '#6b7280';
      default:
        return '#6b7280';
    }
  };

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <div className='navbar-brand'>
          <img src="/urban-web-host-logo.png" alt="" width={161} height={22} style={{ marginTop: '10px' }} />
        </div>
        <div className="navbar-brand">
          <h2>Task Manager</h2>
        </div>

        <div className="navbar-user">

          {/* Attendance Controls */}
          <div className="attendance-controls">
            <button
              className="clock-btn clock-in"
              onClick={handleClockIn}
              disabled={loadingAttendance || attendanceStatus === "Clocked In"}
            >
              Clock In
            </button>

            <button
              className="clock-btn clock-out"
              onClick={handleClockOut}
              disabled={loadingAttendance || attendanceStatus !== "Clocked In"}
            >
              Clock Out
            </button>

            <span className="attendance-status">{attendanceStatus}</span>
          </div>

          <div className="status-selector">
            <button
              className="status-button"
              onClick={() => setShowStatusMenu(!showStatusMenu)}
            >
              <span
                className="status-indicator"
                style={{ backgroundColor: getStatusColor(currentStatus) }}
              />
              <span>{currentStatus}</span>
              <FiChevronDown />
            </button>
            {showStatusMenu && (
              <div className="status-menu">
                {statuses.map(status => (
                  <button
                    key={status}
                    onClick={() => handleStatusChange(status)}
                    className={currentStatus === status ? 'active' : ''}
                  >
                    <span
                      className="status-indicator"
                      style={{ backgroundColor: getStatusColor(status) }}
                    />
                    {status}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="user-info user-info-clickable" onClick={() => setShowProfile(true)} title="View My Profile">
            <FiUser size={20} />
            <span>{user?.username}</span>
            <span className="user-role">({user?.role})</span>
          </div>

          <button className="logout-btn" onClick={handleLogout}>
            <FiLogOut size={20} />
            Logout
          </button>

        </div>
      </div>

      <MyProfile isOpen={showProfile} onClose={() => setShowProfile(false)} />
    </nav>
  );
};

export default Navbar;
